import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import { useAtom } from "jotai";
import "../sideMenu/style.scss";
import { useTranslation } from "react-i18next";
import EVCSUploadMethod from "./EVCSUploadMethod";
import { AccordionItem } from "../AccordianItem";
import { useForm } from "@mantine/form";
import { componentsAtom } from "../../utils/atoms/componentsAtom";
import displayError from "../../constants/utils";
import MGAAPI from "../../api/index";
import _ from "lodash";
import { addedStepsAtom } from "../../utils/atoms/addedStepsAtom";
import {
  flattenCountComponentDTOList,
  flattenDesignCountComponent,
  projectDetailsAtom,
} from "../../utils/atoms/projectDetailsAtom";
import { componentStatusAtom } from "../../utils/atoms/componentStatus";
import { useMantineColorScheme, Card, Text, Grid, TextInput } from "@mantine/core";
import {
  useAssessmentRouteParams,
  useCurrentDesignComponentLists,
  formatInputValue,
  mgaRegex,
  responsiveCol,
} from "../../constants/helper";
import { parseISOToLocalDateAtMidnightUTC } from "../../utils/dateHelper";
import {
  toUtcISOStringWithStartTime,
  getUtcMidnightIso,
  getDefaultEvcsStartDate,
} from "../../utils/helpers";
import { helpContextMenuAtom } from "../../utils/atoms/helpContextMenuAtom";
import { helpAtom } from "../../utils/atoms/helpAtom";
import { useHelpFocusBlur } from "../../utils/useHelpFocusBlur";

const MGA_API = new MGAAPI();

const EVCSMethod = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const {
    evcsComponents = [],
    projectDetails,
    initialTab = "",
    initialSelectedBuildingType,
    saveComponents,
    setLoadingComp,
  } = props;

  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const [, setComponentStatus] = useAtom(componentStatusAtom);
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(
    projectDetails?.designComponentsList
  );

  const [addedStepsSeries] = useAtom(addedStepsAtom);
  const [componentDetails, setComponentDetails] = useAtom(componentsAtom);
  const [openSteps, setOpenSteps] = useState(false);
  const [dataSource, setDataSource] = useState(initialTab || "load");
  const [, setProjectDetails] = useAtom(projectDetailsAtom);
  const { colorScheme } = useMantineColorScheme();
  const prevDataSourceRef = useRef(dataSource);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);


  const evcsAtom = componentDetails?.evcs;
  const evcsParams = evcsAtom?.componentAttributes?.evcsParameters;
  const loadStartDate =
    componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
      ?.startDate;

  // ─── Form ───────────────────────────────────────────────────────────────────
  const form = useForm({
    key: JSON.stringify(evcsAtom),
    initialValues: {
      ...evcsAtom,
      componentAttributes: {
        evcsParameters: {
          profileDetail: {
            ...evcsParams?.profileDetail,
            startTime: evcsParams?.profileDetail?.startTime || "00:00",
            startDate: evcsParams?.profileDetail?.startDate
              ? evcsParams.profileDetail.startDate instanceof Date
                ? evcsParams.profileDetail.startDate
                : parseISOToLocalDateAtMidnightUTC(evcsParams.profileDetail.startDate)
              : getDefaultEvcsStartDate(loadStartDate),
          },
          profileFile: evcsParams?.profileFile || [],
        },
      },
    },
    validate: {
      economicAttributes: {
        evcsParameters: {
          CAPEX: (value) =>
            value === null || String(value).trim() === "" || Number(value) === 0
              ? null
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(value)
              ? t("evcs.validation.capexRange")
              : null,
          OM: (value) =>
            value === null || String(value).trim() === "" || Number(value) === 0
              ? null
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(value)
              ? t("evcs.validation.omRange")
              : null,
          Replacement: (value) =>
            value === null || String(value).trim() === "" || Number(value) === 0
              ? null
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(value)
              ? t("evcs.validation.replacementRange")
              : null,
          LifetimeYear: (value) =>
            value === null || String(value).trim() === "" || Number(value) === 0
              ? null
              : Number(value) < 0 || Number(value) > 25
              ? t("evcs.validation.lifetimeYearRange")
              : null,
        },
      },
      componentAttributes: {
        evcsParameters: {
          profileDetail: {
            evcsFile: (value) =>
              dataSource !== "ashrae" &&
              (value && evcsParams?.profileDetail?.evcsFileClicked
                ? t("loadMethod.validation.loadFileClick")
                : evcsParams?.profileFile?.length || value
                ? null
                : t("evcs.validation.profileFileRequired")),  
            startDate: (value) => {
              const recurrence = evcsParams?.profileDetail?.recurrence;
              if (dataSource === "ashrae" || recurrence === "DAILY") return null;
              return value ? null : t("loadMethod.validation.startDateRequired");
            },
            startTime: (value) => {
              const recurrence = evcsParams?.profileDetail?.recurrence;
              if (dataSource === "ashrae" || recurrence === "DAILY") return null;
              return value ? null : t("loadMethod.validation.startTimeRequired");
            },
            timeResolution: (value) =>
              dataSource !== "ashrae" &&
              (value ? null : t("loadMethod.validation.timeResolutionRequired")),
            units: (value) =>
              dataSource !== "ashrae" &&
              (value ? null : t("loadMethod.validation.unitsRequired")),
          },
        },
      },
    },
  });

  const { setValues } = form;

  useEffect(() => {
    if (!form.isTouched()) return;
    setComponentStatus((prev) => ({
      ...prev,
      evcs: { isEditing: true, completed: false },
    }));
  }, [form.values]);

  // ─── State updater ──────────────────────────────────────────────────────────
  const updateEvcsState = useCallback(
    (category, key, value) => {
      setComponentDetails((prev) => {
        const newState = { ...prev, currentComponent: "evcs" };
        if (!key) {
          _.set(newState, `evcs.${category}`, value);
          setComponentStatus((s) => ({
            ...s,
            evcs: { isEditing: true, completed: false },
          }));
        } else {
          _.set(newState, `evcs.${category}.${key}`, value);
        }
        const detail =
          newState.evcs?.componentAttributes?.evcsParameters?.profileDetail;
        if (detail) {
          detail.startDate = detail.startDate
            ? new Date(detail.startDate)
            : getDefaultEvcsStartDate(loadStartDate);
        }
        setValues(newState?.evcs);
        return newState;
      });
    },
    [loadStartDate, setComponentDetails, setComponentStatus, setValues]
  );

  // ─── projectDetails sync helper ─────────────────────────────────────────────
  const updateProjectDetailsWithComponentAttributes = useCallback(
    (apiResponse, dvId) => {
      setProjectDetails((prevDetails) => {
        const updatedDesignComponentsList = prevDetails?.designComponentsList?.map(
          (design) => ({
            ...design,
            designVersionComponents: design?.designVersionComponents?.map((version) => {
              if (String(version.designVersionId) !== String(dvId)) return version;
              return {
                ...version,
                componentDTOList: version?.componentDTOList?.map((component) =>
                  String(component.componentId) === String(apiResponse?.componentId)
                    ? { ...component, ...apiResponse }
                    : component
                ),
              };
            }),
          })
        );
        return { ...prevDetails, designComponentsList: updatedDesignComponentsList };
      });
    },
    []
  );

   const containerRef = useRef(null);
    const accordianRef = useRef(null);
  
    useHelpFocusBlur(accordianRef, setHelperAtom, helperAtom);
    const handleContainerClick = (event, cardId) => {
      const interactiveTags = ["INPUT", "SELECT", "TEXTAREA", "BUTTON", "LABEL"];
      if (interactiveTags.includes(event.target.tagName)) return;
      const cardElement = document.querySelector(`[card-id=${cardId}]`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
        cardElement.style.transition = "background-color 0.5s ease";
        cardElement.style.border = "2px solid #42b4e6";
        setTimeout(() => {
          cardElement.style.border = "";
        }, 2000);
      }
    };
  
    const handleContainerKeyDown = (event, cardId) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleContainerClick(event, cardId);
      }
    };
  
    const handleBlur = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setHelperAtom({
          ...helperAtom,
          isFocused: false,
          helpSection: {
            title: "",
            description: "",
            type: "",
            other: "",
          },
        });
      }
    };
    const handleFocus = (event) => {
      const dataId = event.target.getAttribute("data-id");
      if (dataId) {
        const cardElement = document.querySelector(`[card-id="${dataId}"]`);
        if (cardElement) {
          cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
          cardElement.style.transition = "background-color 0.5s ease";
          cardElement.style.border = "2px solid #42b4e6";
          setTimeout(() => {
            cardElement.style.border = "";
          }, 2000);
        }
      }
    };
  
    const [helpMenuData] = useAtom(helpContextMenuAtom);
  
    const getFieldAttributes = (fieldKey) => {
      const field = helpMenuData?.evcs?.[fieldKey];
      return {
        title: field?.title || "",
        "data-description": field?.description || "",
        // placeholder: field?.placeholder || "",
      };
    };
  
    useEffect(() => {
      const inputElements = containerRef.current.querySelectorAll(
        "input, select, textarea, file"
      );
  
      inputElements.forEach((inputElement) => {
        inputElement.addEventListener("focus", handleFocus);
        inputElement.addEventListener("blur", handleBlur);
      });
  
      return () => {
        inputElements.forEach((inputElement) => {
          inputElement.removeEventListener("focus", handleFocus);
          inputElement.removeEventListener("blur", handleBlur);
        });
      };
    }, []);

  // ─── Save / upload ──────────────────────────────────────────────────────────
  const handleEvcsUpload = async () => {
    setLoadingComp(true);
    const filteredComponent = selectedDesignAndDesignVersion?.componentDTOList?.find(
      (comp) => comp.componentType === "EVCS"
    );
    const profileDetail = evcsParams?.profileDetail || {};
    const recurrence = profileDetail.recurrence || "YEARLY";
    const isDailyRecurrence = recurrence === "DAILY";
    const payload = {
      projectId,
      designId,
      designVersionId,
      componentType: "EVCS",
      componentId: filteredComponent ? filteredComponent.componentId : -1,
      componentAttributes: {
        evcsParameters: {
          profileDetail: {
            componentType: "EVCS",
            recurrence,
            profileFileName: profileDetail.profileFileName || null,
            startDate:
              dataSource === "ashrae"
                ? null
                : isDailyRecurrence
                ? getUtcMidnightIso(loadStartDate)
                : toUtcISOStringWithStartTime(
                    profileDetail.startDate,
                    profileDetail.startTime
                  ),
            startTime: isDailyRecurrence ? "00:00" : (profileDetail.startTime || "00:00"),
            timeResolution: profileDetail.timeResolution || 15,
            units: profileDetail.units || "kW",
            profileDataLoc:
              dataSource === "ashrae" ? profileDetail.profileDataLoc : null,
          },
          profileFile: dataSource === "ashrae" ? [] : evcsParams?.profileFile || [],
        },
      },
      economicAttributes: evcsAtom?.economicAttributes || {
        evcsParameters: { CAPEX: 0, Replacement: 0, OM: 0, LifetimeYear: 0 },
      },
      datasource: dataSource === "ashrae" ? "Ashrae" : "Upload",
    };

    try {
      updateEvcsState(
        "componentAttributes.evcsParameters.profileDetail",
        "evcsFile",
        null
      );
      MGA_API.importLoad(payload)
        .then((resp) => {
          setLoadingComp(false);
          displayError("success",  t("evcs.status.saveSuccess"));
          if (resp?.componentId) {
            MGA_API.editProject(projectId).then((res) => {
              setProjectDetails((prev) => ({
                ...prev,
                userId: res.userId || "",
                currencyCode: res?.projectDTO?.currencyCode || "$",
                projectDTO: res.projectDTO || {},
                componentDTOList:
                  flattenCountComponentDTOList(res.countComponentDTOList) || [],
                designWithVersionsDTOList: res.designWithVersionsDTOList || [],
                locationDTO: res.locationDTO || {},
                designComponentsList:
                  flattenDesignCountComponent(res?.designComponentsList) || [],
              }));
              updateProjectDetailsWithComponentAttributes(resp, designVersionId);
              setComponentStatus((prev) => ({
                ...prev,
                evcs: { completed: true, isEditing: false },
              }));
            });
          }
        })
        .catch((error) => {
          setLoadingComp(false);
          displayError(
            "error",
            error.response?.data?.message || t("evcs.status.saveError")
          );
        });
    } catch (error) {
      setLoadingComp(false);
      const msg = error.response?.data || t("loadMethod.status.uploadError");
      displayError("error", msg.message || msg);
    }
  };

  const handleEvcsMethod = form.onSubmit(() => {
    handleEvcsUpload();
  });

  useImperativeHandle(ref, () => ({ handleEvcsMethod }));

  // ─── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    setOpenSteps(evcsComponents.length > 0 || true);
  }, [evcsComponents, designVersionId]);

  useEffect(() => {
    const evcsAPIData = _.find(selectedDesignAndDesignVersion.componentDTOList, {
      componentType: "EVCS",
    });
    const isAshrae = evcsAPIData?.datasource?.toLowerCase() === "ashrae";
    setDataSource(isAshrae ? "ashrae" : initialTab || "load");
  }, [initialTab, designVersionId]);

  useEffect(() => {
    if (prevDataSourceRef.current === "ashrae" && dataSource !== "ashrae") {
      updateEvcsState(
        "componentAttributes.evcsParameters.profileDetail",
        "profileDataLoc",
        null
      );
    }
    prevDataSourceRef.current = dataSource;
  }, [dataSource]);

  // ─── Economic attribute updater ─────────────────────────────────────────────
  const handleEconomicChange = (key, value) => {
    setComponentDetails((prev) => ({
      ...prev,
      currentComponent: "evcs",
      evcs: {
        ...prev.evcs,
        economicAttributes: {
          evcsParameters: {
            ...prev.evcs?.economicAttributes?.evcsParameters,
            [key]: value,
          },
        },
      },
    }));
    form.setFieldValue(`economicAttributes.evcsParameters.${key}`, value);
  };

  // ─── Accordion A — method selector ──────────────────────────────────────────
  const selectedCardBorder = "2px solid #3dcd58";
  const unselectedCardBorder = "2px solid #e0e0e0";
  const selectedBoxShadow = "0 0 0 2px #3dcd5833";

  const handleTabClick = (tab) => {
    if (!tab) return;
    setDataSource(tab);
    setOpenSteps(true);
    updateEvcsState("datasource", null, tab);
  };

  const header = () => (
    <span>
      <span className="stepText col-md-1 px-0">
        {`Step ${addedStepsSeries.indexOf("evcs") + 1}`}: A.
      </span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        {t("evcs.methodSelectorTitle")}
      </span>
    </span>
  );

  const header2 = () => (
    <span>
      <span className="stepText col-md-1 px-0">
        {`Step ${addedStepsSeries.indexOf("evcs") + 1}`}: C.
      </span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        {t("evcs.investmentCharges")}
      </span>
    </span>
  );

  const children2 = () => (
    <div       
     ref={accordianRef}
      onClick={(e) => handleContainerClick(e, "accordion-evcs-2")}
      onKeyDown={(e) => handleContainerKeyDown(e, "accordion-evcs-2")}
      data-id="accordion-evcs-2"
      role="button"
      tabIndex={0}
      >
    <Grid gutter="md" className="mt-2 mb-2" >
      <Grid.Col {...responsiveCol}>
        <TextInput
          {...getFieldAttributes("field-10")}
          data-id="field-10"
          label={t("evcs.capex.label")}
          placeholder={t("evcs.capex.placeholder")}
          {...form.getInputProps("economicAttributes.evcsParameters.CAPEX")}
          onChange={(e) => {
            const value = formatInputValue(e.target.value);
            handleEconomicChange("CAPEX", value);
          }}
          rightSection={<div style={{ padding: "4px" }}>{projectDetails?.projectDTO?.currencyCode || "$"}/kW</div>}
          rightSectionProps={{ style: { pointerEvents: "none" } }}
          rightSectionWidth={60}
        />
      </Grid.Col>
      <Grid.Col {...responsiveCol}>
        <TextInput
          {...getFieldAttributes("field-11")}
          data-id="field-11"
          label={t("evcs.om.label")}
          placeholder={t("evcs.om.placeholder")}
          {...form.getInputProps("economicAttributes.evcsParameters.OM")}
          onChange={(e) => {
            const value = formatInputValue(e.target.value);
            handleEconomicChange("OM", value);
          }}
          rightSection={<div style={{ padding: "4px" }}>{projectDetails?.projectDTO?.currencyCode || "$"}/kW</div>}
          rightSectionProps={{ style: { pointerEvents: "none" } }}
          rightSectionWidth={60}
        />
      </Grid.Col>
      <Grid.Col {...responsiveCol}>
        <TextInput
          {...getFieldAttributes("field-12")}
          data-id="field-12"
          label={t("evcs.replacement.label")}
          placeholder={t("evcs.replacement.placeholder")}
          {...form.getInputProps("economicAttributes.evcsParameters.Replacement")}
          onChange={(e) => {
            const value = formatInputValue(e.target.value);
            handleEconomicChange("Replacement", value);
          }}
          rightSection={<div style={{ padding: "4px" }}>{projectDetails?.projectDTO?.currencyCode || "$"}/kW</div>}
          rightSectionProps={{ style: { pointerEvents: "none" } }}
          rightSectionWidth={60}
        />
      </Grid.Col>
      <Grid.Col {...responsiveCol}>
        <TextInput
          {...getFieldAttributes("field-13")}
          data-id="field-13"
          label={t("evcs.lifeTimeYears.label")}
          placeholder={t("evcs.lifeTimeYears.placeholder")}
          {...form.getInputProps("economicAttributes.evcsParameters.LifetimeYear")}
          onChange={(e) => {
            handleEconomicChange("LifetimeYear", e.currentTarget.value.trim());
          }}
          rightSection={<div style={{ padding: "4px", marginRight: "1rem" }}>{t("evcs.lifeTimeYears.unit")}</div>}
          rightSectionProps={{ style: { pointerEvents: "none" } }}
        />
      </Grid.Col>
    </Grid>
    </div>
  );

  const children = () => (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: "24px",
        marginBottom: "24px",
        marginTop: "24px",
      }}
      ref={accordianRef}
      onClick={(e) => handleContainerClick(e, "accordion-evcs-1")}
      onKeyDown={(e) => handleContainerKeyDown(e, "accordion-evcs-1")}
      data-id="accordion-evcs-1"
      role="button"
      tabIndex={0}
    >
      <Card
        data-id="field-1"
        shadow="sm"
        padding="xs"
        radius="md"
        onClick={() => {
          handleTabClick("load");
         if (containerRef.current) {
                const cardElement = document.querySelector(
                  '[card-id="field-1"]'
                );
                if (cardElement) {
                  cardElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  cardElement.style.transition = "background-color 0.5s ease";
                  cardElement.style.border = "2px solid #42b4e6";
                  setTimeout(() => {
                    cardElement.style.border = "";
                  }, 2000);
                }
              }
            }}
        style={{
          flex: "0 1 500px",
          minWidth: "180px",
          maxWidth: "220px",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          border:
            dataSource === "load" || dataSource === "electric"
              ? selectedCardBorder
              : unselectedCardBorder,
          boxShadow:
            dataSource === "load" || dataSource === "electric"
              ? selectedBoxShadow
              : undefined,
          transition: "border 0.2s, box-shadow 0.2s",
          textAlign: "center",
          padding: "14px 12px",
        }}
      >
        <Text
          align="center"
          weight={600}
          size="xs"
          className="loadSubHeading mt-1 mb-1"
          style={{ whiteSpace: "normal", fontSize: "16px", paddingBottom: "4px" }}
        >
          {t("evcs.importerTitle")}
        </Text>
        <Text
          align="center"
          size="xs"
          className="loadText mb-1"
          style={{ whiteSpace: "normal", fontSize: "12px" }}
        >
          {t("evcs.importerDescription")}
        </Text>
      </Card>

      <Card
        data-id="field-2"
        shadow="sm"
        padding="xs"
        radius="md"
        data-id="field-evcs-ashrae-card"
        style={{
          flex: "0 1 500px",
          minWidth: "180px",
          maxWidth: "220px",
          cursor: "not-allowed",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          border: unselectedCardBorder,
          transition: "border 0.2s, box-shadow 0.2s",
          textAlign: "center",
          padding: "14px 12px",
          opacity: 0.45,
        }}
         onClick={() => {
         if (containerRef.current) {
                const cardElement = document.querySelector(
                  '[card-id="field-2"]'
                );
                if (cardElement) {
                  cardElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  cardElement.style.transition = "background-color 0.5s ease";
                  cardElement.style.border = "2px solid #42b4e6";
                  setTimeout(() => {
                    cardElement.style.border = "";
                  }, 2000);
                }
              }
            }}
      >
        <Text
          align="center"
          weight={600}
          size="xs"
          className="loadSubHeading mt-1 mb-1"
          style={{ whiteSpace: "normal", fontSize: "16px", paddingBottom: "4px" }}
        >
          {t("evcs.advancedTitle")}
        </Text>
        <Text
          align="center"
          size="xs"
          className="loadText mb-1"
          style={{ whiteSpace: "normal", fontSize: "12px" }}
        >
          {t("importAshry")}
        </Text>
      </Card>
    </div>
  );

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="row" ref={containerRef}>
      <div className="container-fluid">
        {/* Accordion A — method selection */}
        <AccordionItem id={1} header={header()} key={1} children={children()} />

        {/* Accordion B — file upload form + graph */}
        <EVCSUploadMethod
          form={form}
          evcsParameters={evcsParams}
          loadStartDate={
            componentDetails?.load?.componentAttributes?.loadParameters
              ?.profileDetail?.startDate
          }
          updateEvcsState={updateEvcsState}
          openSteps={openSteps}
          projectDetails={projectDetails}
          setLoadingComp={setLoadingComp}
          getFieldAttributes={getFieldAttributes}
        />

        {/* Accordion C — investment details */}
        <AccordionItem id={1} header={header2()} key={3} children={children2()} />
      </div>
    </div>
  );
});

EVCSMethod.displayName = "EVCSMethod";

export default EVCSMethod;
